import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getCompanyUsers } from '../services/userService';

// ── One company-users read for the whole app ────────────────────────────────
// Six pages (Dashboard, Clients, Pipeline, Analytics, Financials, and the
// since-deleted Services page — see the 2026-08-27 dead-code pass)
// each owned a `const [companyUsers, setCompanyUsers] = useState([])` and its
// own `getCompanyUsers(company.id)` effect. Dashboard is the app shell and
// renders one of the others underneath it, so opening any page fired the same
// collection query at least twice — a full `where('companyId','==',…)` scan of
// /users per mount, for a list that changes when someone joins or leaves the
// company and effectively never otherwise.
//
// Same shape as CompanyContext: a provider mounted once in App.js, keyed on
// userProfile.companyId (NOT the userProfile object — AuthContext rebuilds
// that on every user-doc write, a Day/Night toggle included, and depending on
// the object would re-run the query on each theme switch).
//
// `refresh()` is the one escape hatch, for the places that need to see a
// just-created teammate without a reload — Dashboard re-reads when the New
// Record panel opens, because an assignee dropdown is the first thing that
// notices a stale list.
const CompanyUsersContext = createContext({ companyUsers: [], loading: false, error: null, refresh: () => {} });

export function useCompanyUsers() {
  return useContext(CompanyUsersContext);
}

export function CompanyUsersProvider({ children }) {
  const { userProfile } = useAuth();
  const companyId = userProfile?.companyId;

  const [companyUsers, setCompanyUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Guards a `refresh()` that resolves after the company changed (or the
  // provider unmounted) from writing a previous company's roster into state.
  const companyIdRef = useRef(companyId);
  companyIdRef.current = companyId;

  const load = useCallback(async (targetId) => {
    if (!targetId) {
      setCompanyUsers([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const users = await getCompanyUsers(targetId);
      if (companyIdRef.current !== targetId) return;
      setCompanyUsers(users);
      setError(null);
    } catch (err) {
      // Preserved verbatim from the six call sites this replaces. The read
      // rejects with permission-denied for any user the /users read rule
      // excludes, and a bare .then() turned that into an uncaught rejection on
      // every page load. The list degrades to empty either way; what this
      // changes is that the app says so instead of throwing past everyone.
      console.error('Failed to load company users:', err && err.code, err && err.message);
      if (companyIdRef.current !== targetId) return;
      setCompanyUsers([]);
      setError(err);
    } finally {
      if (companyIdRef.current === targetId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(companyId);
  }, [companyId, load]);

  const refresh = useCallback(() => load(companyIdRef.current), [load]);

  const value = useMemo(
    () => ({ companyUsers, loading, error, refresh }),
    [companyUsers, loading, error, refresh],
  );

  return (
    <CompanyUsersContext.Provider value={value}>
      {children}
    </CompanyUsersContext.Provider>
  );
}
